import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone, Loader2, ArrowLeft } from 'lucide-react';

const LoginPage = () => {
  const { login, register, loginWithGoogle } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });

  // Get redirect URL from query params or state
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect') || location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let userPayload;
      if (isLogin) {
        userPayload = await login(formData.email, formData.password);
        toast.success(language === 'pt' ? 'Login realizado com sucesso!' : 'Login successful!');
      } else {
        userPayload = await register(formData.name, formData.email, formData.password, formData.phone);
        toast.success(language === 'pt' ? 'Conta criada com sucesso!' : 'Account created successfully!');
      }
      // Role-based redirect: managers go straight to the POS where they
      // actually receive orders, drivers to the driver dashboard, admins to
      // the admin panel — unless an explicit ?redirect= override was passed.
      const role = userPayload?.role;
      let target = redirectUrl;
      if (redirectUrl === '/' || !redirectUrl) {
        if (role === 'restaurant_manager') target = '/pos';
        else if (role === 'driver') target = '/driver';
        else if (role === 'admin' || role === 'super_admin') target = '/admin';
      }
      navigate(target, { replace: true });
    } catch (error) {
      toast.error(error.message || (language === 'pt' ? 'Erro de autenticação' : 'Authentication error'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <Link to="/">
          <Button variant="ghost" className="mb-6 text-[#004E64]">
            <ArrowLeft size={20} className="mr-2" />
            {language === 'pt' ? 'Voltar' : 'Back'}
          </Button>
        </Link>

        <Card className="rounded-3xl border-[#004E64]/5 shadow-lg">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#FF6B35] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">T</span>
            </div>
            <CardTitle className="text-2xl text-[#004E64]">
              {isLogin ? t('login') : t('register')}
            </CardTitle>
            <CardDescription>
              {isLogin 
                ? (language === 'pt' ? 'Entre para fazer seus pedidos' : 'Sign in to place your orders')
                : (language === 'pt' ? 'Crie sua conta para começar' : 'Create your account to get started')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div>
                    <Label htmlFor="name">{t('name')}</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9FA2B4]" size={18} />
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        required
                        className="pl-10 rounded-xl"
                        data-testid="input-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="phone">{t('phone')}</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9FA2B4]" size={18} />
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        className="pl-10 rounded-xl"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="email">{t('email')}</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9FA2B4]" size={18} />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="pl-10 rounded-xl"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">{t('password')}</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9FA2B4]" size={18} />
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    required
                    className="pl-10 rounded-xl"
                    data-testid="input-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full py-6 font-bold"
                data-testid="submit-button"
              >
                {loading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  isLogin ? t('login') : t('register')
                )}
              </Button>
            </form>

            <div className="relative">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-sm text-[#9FA2B4]">
                {language === 'pt' ? 'ou' : 'or'}
              </span>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={loginWithGoogle}
              className="w-full rounded-full py-6 font-semibold border-[#004E64]/10"
              data-testid="google-login-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('loginWithGoogle')}
            </Button>

            <p className="text-center text-[#9FA2B4]">
              {isLogin ? t('noAccount') : t('haveAccount')}{' '}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-[#FF6B35] font-semibold hover:underline"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? t('register') : t('login')}
              </button>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
